/**
 * Printouts (owner, 2026-10-14: "search should cover finding print outs needed and organized especially for a violation"). One list of
 * everything a place prints, built from the same data the Signs and forms, Paper logs and Binder screens read, never a hand list:
 *
 *   sign     every sign or form of the place's state and county (signs.ts signDefsFor + appliesHere: the Signs and forms screen's rows;
 *            the screen narrows them by what the place has and serves, search lists every one that could apply),
 *   log      every paper log of the place's permit (paperSheetTemplates: the Paper logs screen's "Print sheets" list),
 *   binder   the inspection binder PDF and, for a place linked to its health department record, the follow-up page in it,
 *   label    the food date label (Food screen), and the allergen menu (a page to print or share).
 *
 * | P1 One source       | a sign comes from signDefsFor, a paper log from paperSheetTemplates (the screen calls the same function); a test fails
 * |                     | when a sign or log the screens show is not here                                                                          |
 * | P2 Tied by a source | a printout is mapped to an FDA Form 3-A item only where a source in the repo ties them: a sign by the Food Code section
 * |                     | Guide 3-B lists under that item (SIGN_ITEMS, checked against docs/source/research/paa/inspection-form-items.txt), a log by the
 * |                     | form's own records column (inspectionform.ts FORM_ITEMS tags "tpl:<log>"), the date label by its "food:label" tag |
 * | P3 Never invented   | an item with no tie has no printouts and the screens show nothing for it; nothing is guessed from words              |
 * | P4 Private stays    | the health check-in and return-to-work logs are never listed (appsearch.ts SEARCH_PRIVATE_TEMPLATES, T4)            |
 */
import { FORM_ITEMS } from "./inspectionform.ts";
import type { PermitType } from "./model.ts";
import { appliesHere, KIND_WORD, signDefsFor } from "./signs.ts";
import { templatesFor, type SheetTemplate } from "./templates.ts";
import type { UsState } from "./states/types.ts";

export type PrintAction = "Open" | "Print" | "Download";
export type PrintKind = "sign" | "log" | "binder" | "label" | "followup" | "menu";

export interface Printout {
  /** Stable: "sign:handwashing-sign", "log:cold_units", "binder". */
  id: string;
  kind: PrintKind;
  title: string;
  /** What it is, one line. */
  what: string;
  /** Where it goes, one line ("At each handwashing sink"). */
  where: string;
  /** The one action on the row. */
  action: PrintAction;
  /** The web's path, and the phone's route when the phone has one (paper logs are printed on the web only). */
  web: string;
  phone?: string;
  /** Extra words that find it (lowercase). */
  words: string[];
  /** The FDA Form 3-A items it is tied to (P2). */
  items: number[];
  /** The tie, in words, for the items ("Food Code 6-301.14"). */
  tie?: string;
  /** Only a manager or above prints it (the Paper logs screen's Print section). */
  manager?: boolean;
  /** The template id for a paper log. */
  templateId?: string;
}

export interface PrintPlace {
  state?: UsState;
  county?: string | undefined;
  permitType: PermitType;
  /** The location is linked to its health department's record (Location.officialPlaceKey): the follow-up page exists. */
  linked?: boolean;
}

/** The health check-in and return-to-work logs are private (T4): never a search result, never a printout row. */
export const PRINT_PRIVATE_TEMPLATES: ReadonlySet<string> = new Set(["health_checkin", "ready_to_work"]);

/** The Paper logs screen's "Which log?" list: the permit's logs but the checklist, the ones the place needs first (Paper.tsx Print). */
export function paperSheetTemplates(permitType: PermitType): SheetTemplate[] {
  return templatesFor(permitType).filter((t) => t.id !== "task_done");
}

/**
 * Sign → FDA Form 3-A item, each tied by the Food Code section that Guide 3-B (Annex 7) lists under that item as an "Applicable Code
 * Section", and that the sign's own catalog citation names (test: both read from the files, with a control).
 */
export const SIGN_ITEMS: readonly { sign: string; item: number; cite: string }[] = [
  { sign: "handwashing-sign", item: 10, cite: "6-301.14" },
  { sign: "consumer-advisory", item: 25, cite: "3-603.11" },
  { sign: "allergen-notice", item: 37, cite: "3-602.12" },
  { sign: "clean-tableware-notice", item: 47, cite: "3-304.16" },
  { sign: "employee-health-agreement", item: 3, cite: "2-103.11" },
  { sign: "employee-health-agreement", item: 4, cite: "2-201.11" },
];

/** Items whose records the form lists under a log (FORM_ITEMS tags "tpl:<id>"). */
function itemsOfLog(id: string): number[] {
  return FORM_ITEMS.filter((f) => f.tags?.includes(`tpl:${id}`)).map((f) => f.n);
}
const itemsOfTag = (tag: string): number[] => FORM_ITEMS.filter((f) => f.tags?.includes(tag)).map((f) => f.n);

const words = (s: string) => s.toLowerCase().split(/\s+/).filter(Boolean);
const PERIOD: Record<SheetTemplate["period"], string> = { day: "day", week: "week", month: "month", event: "event" };

/** Everything this place prints, in a useful order: signs (required first), the binder, the date label, the paper logs. */
export function printouts(p: PrintPlace): Printout[] {
  const out: Printout[] = [];
  for (const d of signDefsFor(p.state)) {
    if (!appliesHere(d, p, p.state)) continue;
    const ties = SIGN_ITEMS.filter((s) => s.sign === d.id);
    out.push({
      id: `sign:${d.id}`, kind: "sign", title: d.title.en, what: KIND_WORD[d.kind], where: d.where,
      action: d.printable || d.prop65 ? "Print" : "Open", web: `/signs#sign-${d.id}`, phone: "/signs", manager: true,
      words: words(`sign signs poster post posted wall print form ${d.title.en} ${d.title.es}`),
      items: ties.map((t) => t.item), ...(ties.length ? { tie: ties.map((t) => `Food Code ${t.cite}`).join(", ") } : {}),
    });
  }
  out.push({
    id: "binder", kind: "binder", title: "Inspection binder (PDF)", what: "One PDF, the way an inspector reads it", where: "Ready for the inspector",
    action: "Print", web: "/prove", phone: "/prove", manager: true, words: words("binder print pdf inspector inspection proof records audit health department"), items: [],
  });
  if (p.linked) out.push({
    id: "followup", kind: "followup", title: "Inspection follow-up page", what: "What the county cited, and what you did about it", where: "In the inspection binder",
    action: "Open", web: "/inspection", phone: "/inspection", words: words("follow up followup inspection binder page county cited violations fix"), items: [],
  });
  out.push({
    id: "label", kind: "label", title: "Food date label", what: "A date label with a QR, to print", where: "On each container you prep or open",
    action: "Print", web: "/food/label", phone: "/label-food", words: words("label labels sticker date mark day dot prep qr print food"), items: itemsOfTag("food:label"), tie: "the form's food labels record",
  });
  out.push({
    id: "menu", kind: "menu", title: "Allergen menu", what: "The menu with its allergens, to print or share", where: "On the counter or the menu",
    action: "Print", web: "/allergens/menu", manager: true, words: words("allergen allergy menu print qr customers guests"), items: [],
  });
  for (const t of paperSheetTemplates(p.permitType)) {
    if (PRINT_PRIVATE_TEMPLATES.has(t.id)) continue;
    out.push({
      id: `log:${t.id}`, kind: "log", title: `${t.title}: paper sheet`, what: `Paper log, one sheet per ${PERIOD[t.period]}`, where: "On the wall or a clipboard",
      action: "Download", web: `/paper?sheet=${encodeURIComponent(t.id)}#print`, manager: true, templateId: t.id,
      words: words(`paper log sheet sheets print clipboard form ${t.title} ${t.id.replace(/_/g, " ")}`),
      items: itemsOfLog(t.id), ...(itemsOfLog(t.id).length ? { tie: "the form's records column" } : {}),
    });
  }
  return out;
}

const KIND_RANK: Readonly<Record<PrintKind, number>> = { sign: 0, log: 1, label: 2, menu: 3, binder: 4, followup: 5 };

/**
 * The printouts tied to these cited items (P2), signs first then the log that shows the fix, at most `max`. On the phone only what
 * the phone opens (it prints no paper sheets). Nothing tied: an empty list (P3).
 */
export function printoutsForItems(items: readonly number[], p: PrintPlace, platform: "web" | "phone" = "web", max = 4): Printout[] {
  const want = new Set(items);
  if (!want.size) return [];
  return printouts(p)
    .filter((x) => x.items.some((n) => want.has(n)) && (platform === "web" || !!x.phone))
    .sort((a, b) => KIND_RANK[a.kind] - KIND_RANK[b.kind])
    .slice(0, max);
}

/** One printout as a line on the inspection page: what to print and where it goes. The phone path is absent for what the phone does not print. */
export interface PrintLine { id: string; title: string; where: string; action: PrintAction; web: string; phone?: string; tie: string | null }
export const PRINT_FOR_THIS = "Print for this";
/** The phone, under a printout it does not make (a paper log): the website does. */
export const PRINT_ON_WEB = "Print this one from the website.";
export function printLinesFor(items: readonly number[], p: PrintPlace | undefined): PrintLine[] {
  if (!p) return [];
  return printoutsForItems(items, p, "web", 6).map((x) => ({ id: x.id, title: x.title, where: x.where, action: x.action, web: x.web, ...(x.phone ? { phone: x.phone } : {}), tie: x.tie ?? null }));
}
